import { topicRowParts } from '@/lib/formsAnswerValue';

// Mirrors Clinio Forms scoringEngine + domain configs for SF-36 / WHODAS radar charts.

export type DomainScore = {
  domain: string;
  score: number;
  color: string;
};

export type ScoreResult = {
  domainScores: DomainScore[];
  totalScore: number;
};

type QuestionOption = {
  value: string;
  label: string;
  score?: number;
};

type QuestionDefinition = {
  id: string;
  text: string;
  type: string;
  category_id?: string;
  options?: QuestionOption[];
  scoring?: { method?: string; weight?: number; max_score?: number };
};

type ScoringCategory = {
  category_id: string;
  name: string;
  color?: string;
  calculation_method?: 'average' | 'sum';
};

export type ScoringConfig = {
  categories?: ScoringCategory[];
};

const CHART_SERIES = [
  'var(--teal)',
  'var(--green)',
  'var(--amber)',
  'var(--coral)',
  'var(--teal-700)',
  'var(--red)',
] as const;

const SF36_DOMAINS: Record<string, { items: number[]; color: string }> = {
  'Physical Functioning': { items: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12], color: CHART_SERIES[0] },
  'Role-Physical': { items: [13, 14, 15, 16], color: CHART_SERIES[1] },
  'Bodily Pain': { items: [21, 22], color: CHART_SERIES[2] },
  'General Health': { items: [1, 33, 34, 35, 36], color: CHART_SERIES[3] },
  Vitality: { items: [23, 27, 29, 31], color: CHART_SERIES[4] },
  'Social Functioning': { items: [20, 32], color: CHART_SERIES[5] },
  'Role-Emotional': { items: [17, 18, 19], color: CHART_SERIES[0] },
  'Mental Health': { items: [24, 25, 26, 28, 30], color: CHART_SERIES[2] },
};

const WHODAS_DOMAINS: Record<string, { items: string[]; color: string }> = {
  Cognition: { items: ['s3', 's6'], color: CHART_SERIES[0] },
  Mobility: { items: ['s1', 's7'], color: CHART_SERIES[1] },
  'Self-care': { items: ['s8', 's9'], color: CHART_SERIES[2] },
  'Getting along': { items: ['s10', 's11'], color: CHART_SERIES[3] },
  'Life activities': { items: ['s2', 's12'], color: CHART_SERIES[4] },
  Participation: { items: ['s4', 's5'], color: CHART_SERIES[5] },
};

function calculateSF36Scores(
  questions: QuestionDefinition[],
  responses: Record<string, unknown>,
): ScoreResult {
  const domainScores: DomainScore[] = [];

  for (const [domainName, domainConfig] of Object.entries(SF36_DOMAINS)) {
    const scores: number[] = [];

    for (const itemNumber of domainConfig.items) {
      const questionId = `sf36_${itemNumber}`;
      const question = questions.find((q) => q.id === questionId);
      const response = responses[questionId];

      if (question && response !== undefined && response !== null) {
        const selectedOption = question.options?.find((opt) => opt.value === response);
        if (selectedOption?.score !== undefined) {
          scores.push(selectedOption.score);
        }
      }
    }

    const domainScore =
      scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;

    domainScores.push({
      domain: domainName,
      score: Math.round(domainScore * 10) / 10,
      color: domainConfig.color,
    });
  }

  const totalScore =
    domainScores.length > 0
      ? domainScores.reduce((sum, d) => sum + d.score, 0) / domainScores.length
      : 0;

  return { domainScores, totalScore: Math.round(totalScore * 10) / 10 };
}

function calculateWHODASScores(
  questions: QuestionDefinition[],
  responses: Record<string, unknown>,
): ScoreResult {
  const domainScores: DomainScore[] = [];

  for (const [domainName, domainConfig] of Object.entries(WHODAS_DOMAINS)) {
    const scores: number[] = [];

    for (const itemSuffix of domainConfig.items) {
      const questionId = `whodas_${itemSuffix}`;
      const question = questions.find((q) => q.id === questionId);
      const response = responses[questionId];

      if (question && response !== undefined && response !== null) {
        const selectedOption = question.options?.find((opt) => opt.value === response);
        if (selectedOption?.score !== undefined) {
          scores.push(100 - selectedOption.score * 25);
        }
      }
    }

    const domainScore =
      scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;

    domainScores.push({
      domain: domainName,
      score: Math.round(domainScore * 10) / 10,
      color: domainConfig.color,
    });
  }

  const totalScore =
    domainScores.length > 0
      ? domainScores.reduce((sum, d) => sum + d.score, 0) / domainScores.length
      : 0;

  return { domainScores, totalScore: Math.round(totalScore * 10) / 10 };
}

function calculateCustomScores(
  questions: QuestionDefinition[],
  responses: Record<string, unknown>,
  scoringConfig: ScoringConfig,
): ScoreResult {
  const domainScores: DomainScore[] = [];
  const categories = scoringConfig.categories ?? [];

  for (const category of categories) {
    const domainQuestions = questions.filter((q) => q.category_id === category.category_id);
    const scores: number[] = [];

    for (const question of domainQuestions) {
      const response = responses[question.id];
      if (response === undefined || response === null) continue;

      let score = 0;
      if (question.type === 'topic_row') {
        const scale = topicRowParts(response)?.scale ?? null;
        if (!scale) continue;
        const selectedOption = question.options?.find((opt) => opt.value === scale);
        if (selectedOption?.score === undefined) continue;
        score = selectedOption.score;
      } else if (question.type === 'multiple_choice' || question.type === 'checkbox') {
        const selectedOption = question.options?.find((opt) => opt.value === response);
        if (selectedOption?.score !== undefined) {
          score = selectedOption.score;
        }
      } else if (question.type === 'scale') {
        score = typeof response === 'number' ? response : parseFloat(String(response)) || 0;
      }

      if (question.scoring?.method === 'reverse') {
        const max = question.scoring.max_score ?? 100;
        score = max - score;
      }
      if (question.scoring?.weight) {
        score *= question.scoring.weight;
      }
      scores.push(score);
    }

    let domainScore = 0;
    if (scores.length > 0) {
      domainScore =
        category.calculation_method === 'sum'
          ? scores.reduce((sum, s) => sum + s, 0)
          : scores.reduce((sum, s) => sum + s, 0) / scores.length;
    }

    domainScores.push({
      domain: category.name,
      score: Math.round(domainScore * 10) / 10,
      color: category.color ?? CHART_SERIES[0],
    });
  }

  const totalScore =
    domainScores.length > 0
      ? domainScores.reduce((sum, d) => sum + d.score, 0) / domainScores.length
      : 0;

  return { domainScores, totalScore: Math.round(totalScore * 10) / 10 };
}

export function calculateQuestionnaireScores(
  questions: QuestionDefinition[],
  responses: Record<string, unknown>,
  scoringConfig?: ScoringConfig | null,
): ScoreResult {
  const isSF36 = questions.some((q) => q.id.startsWith('sf36_'));
  const isWHODAS = questions.some((q) => q.id.startsWith('whodas_'));

  if (isSF36) return calculateSF36Scores(questions, responses);
  if (isWHODAS) return calculateWHODASScores(questions, responses);

  if (scoringConfig?.categories?.length) {
    return calculateCustomScores(questions, responses, scoringConfig);
  }

  return { domainScores: [], totalScore: 0 };
}

type StoredScoreDetails = {
  domainScores?: DomainScore[];
  totalScore?: number;
  total_score?: number;
  category_scores?: Record<string, number>;
};

export function resolveAssignmentScores(
  questions: QuestionDefinition[],
  responses: Record<string, unknown>,
  scoringConfig: ScoringConfig | null | undefined,
  scoreDetails: unknown,
): ScoreResult | null {
  if (scoreDetails && typeof scoreDetails === 'object') {
    const stored = scoreDetails as StoredScoreDetails;
    if (Array.isArray(stored.domainScores) && stored.domainScores.length > 0) {
      return {
        domainScores: stored.domainScores,
        totalScore:
          typeof stored.totalScore === 'number'
            ? stored.totalScore
            : typeof stored.total_score === 'number'
              ? stored.total_score
              : stored.domainScores.reduce((sum, d) => sum + d.score, 0) /
                stored.domainScores.length,
      };
    }
    if (stored.category_scores && Object.keys(stored.category_scores).length > 0) {
      const domainScores = Object.entries(stored.category_scores).map(([domain, score], i) => ({
        domain,
        score,
        color: CHART_SERIES[i % CHART_SERIES.length],
      }));
      return {
        domainScores,
        totalScore:
          typeof stored.total_score === 'number'
            ? stored.total_score
            : domainScores.reduce((sum, d) => sum + d.score, 0) / domainScores.length,
      };
    }
  }

  const calculated = calculateQuestionnaireScores(questions, responses, scoringConfig);
  return calculated.domainScores.length > 0 ? calculated : null;
}
